import { Routes } from '@angular/router';
import {
  APP_SHELL_ROUTE_DATA_KEY,
  buildMeinProfilRoute,
  ErrorMessagePlainComponent,
  LOCALIZED_SHARED_ROUTES,
  SHARED_ROUTE_IDS,
} from '@lexplus-shared-lib';
import { AuthPagesRoutes, IsLoggedInGuard, IsLoggedInMatchGuard, RequiresPermissionGuard } from '@lexplus-authentication';
import { AppShellComponent } from './global/components/app-shell/app-shell.component';
import { DashboardPageRoute } from './features/dashboard/pages/dashboard-page.routes';
import { AuswertungenPageRoute } from './features/auswertungen/auswertungen-page.routes';
import { LOCALIZED_USER_ROUTES, USER_ROUTES_IDS } from './localized-user-routes-const';
import { LexplusPermissionsEnum } from '@lexplus-authentication-api';
import { AufgabenPageRoute } from './features/aufgaben/aufgaben-page.routes';
import { AdministrationPageRoute } from './features/administration/administration-page.routes';
import { DokumentenManagerPageRoute } from './features/dokumenten-manager/dokumenten-manager-page.routes';
import { UserDetailDrawerRoute } from './global/functions/user-detail-drawer-route.function';

export function LocalizedAppRoutes(localeId: string): Routes {
  const locale = localeId?.split('-')[0] || 'en';

  return [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.DASHBOARD][locale],
    },
    AuthPagesRoutes(localeId),
    {
      path: '',
      component: AppShellComponent,
      data: { [APP_SHELL_ROUTE_DATA_KEY]: true },
      children: AppShellRoutes(localeId),
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ERROR][locale],
      // Referenced directly rather than lazily: lexplus-shared-lib is already in this
      // app's initial bundle (NGXS states, app shell, guards), so a dynamic import of
      // its barrel would emit no separate chunk.
      component: ErrorMessagePlainComponent,
      data: { routeId: SHARED_ROUTE_IDS.ERROR },
    },
    {
      path: '**',
      redirectTo: `${LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ERROR][locale]}?errorNumber=404`,
    },
  ];
}

/** The pages rendered inside the app shell, including the error page for signed-in users. */
function AppShellRoutes(localeId: string): Routes {
  const locale = localeId?.split('-')[0] || 'en';

  return [
    DashboardPageRoute(localeId),
    {
      path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_COMPLIANCE_OVERVIEW][locale],
      loadComponent: () =>
        import('./features/compliance-uebersicht/pages/compliance-uebersicht-page/compliance-uebersicht-page.component').then(
          (m) => m.ComplianceUebersichtPageComponent,
        ),
      canActivate: [IsLoggedInGuard, RequiresPermissionGuard],
      data: {
        routeId: USER_ROUTES_IDS.USER_COMPLIANCE_OVERVIEW,
        requiredPermission: [LexplusPermissionsEnum.UserComplianceuebersichtAlle, LexplusPermissionsEnum.UserComplianceuebersichtEigene],
      },
      children: [
        // The Archiv tab's rows open the archived kind and the Individuelle Anforderungen tab's cards
        // their own kind, which no active requirement leads to.
        UserDetailDrawerRoute(
          'anforderung',
          LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.DETAILS][locale],
          USER_ROUTES_IDS.USER_COMPLIANCE_OVERVIEW,
          locale,
          ['archivierteAnforderung', 'individuelleAnforderung'],
        ),
      ],
    },
    AufgabenPageRoute(localeId),
    AuswertungenPageRoute(localeId),
    AdministrationPageRoute(localeId),
    DokumentenManagerPageRoute(localeId),
    {
      path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS][locale],
      canActivate: [IsLoggedInGuard],
      canActivateChild: [IsLoggedInGuard],
      data: { routeId: USER_ROUTES_IDS.USER_DOWNLOADS },
      // One child per tab, each loading the same page component and naming itself through
      // `routeId` - which is what the tab strip reads to decide which tab is open. Each tab
      // carries its own permission: the Newsletter tab none, because it only ever shows the
      // user's own newsletters and a subscriber without any download right must reach it.
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS_NEWSLETTER][locale], // Default tab
        },
        {
          path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS_NEWSLETTER][locale],
          loadComponent: () =>
            import('./features/downloads/pages/downloads-page/downloads-page.component').then((m) => m.DownloadsPageComponent),
          data: { routeId: USER_ROUTES_IDS.USER_DOWNLOADS_NEWSLETTER },
        },
        {
          path: `${LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS_NEWSLETTER][locale]}/:newsletterId`,
          loadComponent: () =>
            import('./features/downloads/pages/newsletter-detail-page/newsletter-detail-page.component').then(
              (m) => m.NewsletterDetailPageComponent,
            ),
          data: { routeId: USER_ROUTES_IDS.USER_DOWNLOADS_NEWSLETTER_DETAIL },
        },
        {
          path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS_BERICHTE][locale],
          loadComponent: () =>
            import('./features/downloads/pages/downloads-page/downloads-page.component').then((m) => m.DownloadsPageComponent),
          canActivate: [RequiresPermissionGuard],
          data: {
            routeId: USER_ROUTES_IDS.USER_DOWNLOADS_BERICHTE,
            requiredPermission: LexplusPermissionsEnum.UserDownloadsBegehungsbericht,
          },
        },
        {
          path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS_AUTOMATISIERTE_BERICHTE][locale],
          loadComponent: () =>
            import('./features/downloads/pages/downloads-page/downloads-page.component').then((m) => m.DownloadsPageComponent),
          canActivate: [RequiresPermissionGuard],
          data: {
            routeId: USER_ROUTES_IDS.USER_DOWNLOADS_AUTOMATISIERTE_BERICHTE,
            requiredPermission: LexplusPermissionsEnum.UserDownloadsAutomatisierteberichte,
          },
        },
        {
          path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_DOWNLOADS_GESETZESLISTEN][locale],
          loadComponent: () =>
            import('./features/downloads/pages/downloads-page/downloads-page.component').then((m) => m.DownloadsPageComponent),
          canActivate: [RequiresPermissionGuard],
          data: { routeId: USER_ROUTES_IDS.USER_DOWNLOADS_GESETZESLISTEN, requiredPermission: LexplusPermissionsEnum.UserDownloadsBasis },
        },
      ],
    },
    {
      path: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.USER_FAQ][locale],
      loadComponent: () => import('./features/faq/pages/faq-page/faq-page.component').then((m) => m.FaqPageComponent),
      canActivate: [IsLoggedInGuard],
      data: { routeId: USER_ROUTES_IDS.USER_FAQ },
    },
    buildMeinProfilRoute(locale, [IsLoggedInGuard]),
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ERROR][locale],
      component: ErrorMessagePlainComponent,
      canMatch: [IsLoggedInMatchGuard],
      data: { routeId: SHARED_ROUTE_IDS.ERROR },
    },
  ];
}
